/**
 * Talking to the embed host.
 *
 * The public chat runs in two places: the standalone `/c/:publicId` page, and an
 * iframe injected by `public/embed.js` on a customer's website. Everything that
 * depends on which one we are in goes through here.
 *
 * Two directions:
 *  - **up** (`postToParent`) — what the chat tells the launcher: the session id, the
 *    unread count, "a message just arrived", the state of the voice leg.
 *  - **down** (`readIntentFromUrl` / `onParentIntent`) — what the host asked for when
 *    it opened the widget: start on the voice leg, and who the visitor is.
 *
 * An intent is **never a trust statement**. Anything the host page says about the
 * visitor arrives here unsigned (the signed path is the connector HMAC, consumed
 * server-side by `/public/start`) and is applied at exactly the trust level of
 * something the visitor typed into the form themselves — which is why a phone handed
 * over this way still has to go through the OTP before anything reads it as verified.
 */

/** Visitor details the host page can hand over (`HeyUpChat({ customer })`). */
export interface EmbedIdentity {
  given_name?: string;
  family_name?: string;
  email?: string;
  phone?: string;
}

/** What the host asked for. Every field is optional — an empty intent is a no-op. */
export interface EmbedIntent {
  /**
   * Three-valued on purpose: `true` puts the visitor on the voice leg, `false` hangs
   * it up, and absent leaves it alone. Flattening the last two together would drop
   * every live call the host merely wanted to identify a visitor on.
   */
  audio?: boolean;
  /**
   * A first message to say on the visitor's behalf ("je voudrais prendre rendez-vous"),
   * so a host page can put a specific question behind its own button instead of opening
   * on an empty box.
   */
  message?: string;
  /** The same, written into the input but not sent — the visitor presses enter. */
  draft?: string;
  /**
   * Open the booking calendar straight away (the host page's "prendre rendez-vous"
   * button), without waiting for the agent to decide to: `message`, when there is one,
   * is shown as what the visitor said, and no turn of the model runs.
   */
  booking?: boolean;
  /**
   * True when this intent was read off the iframe URL rather than pushed live. It is
   * what tells a message that rode in on a page load apart from one the host just asked
   * for: the widget resumes a conversation for 12h, and replaying the URL's message on
   * every page view would talk over the visitor.
   */
  fromUrl?: boolean;
  identity?: EmbedIdentity | null;
  /**
   * `false` records an unsigned phone on the customer without texting anybody. The
   * default is the opposite: a number the host claims is a number nobody proved, and
   * the OTP is the only thing that turns it into one the agent may act on.
   */
  verify?: boolean;
  /**
   * The host signed a contact with the connector secret (`data-user-hash`), so
   * `/public/start` already resolved a trusted customer from it. Only the *signed*
   * contact is trusted, and that is the email when there is one — so this alone does
   * not say the phone is verified, it says which of the two was vouched for.
   */
  signed?: boolean;
}

/** Are we rendered inside the embed iframe (vs. the standalone page)? */
export function inIframe(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.self !== window.top;
  } catch {
    return true; // cross-origin access throws → we are framed
  }
}

/** Send a message to `embed.js`. A no-op on the standalone page. */
export function postToParent(payload: Record<string, unknown>) {
  if (!inIframe()) return;
  try {
    window.parent.postMessage({ source: "heyup", ...payload }, "*");
  } catch {
    /* ignore */
  }
}

/** Ask the embed host to minimize the widget back to its launcher. */
export function minimizeEmbed() {
  postToParent({ type: "close" });
}

/**
 * Tell the host the chat is mounted and listening. `embed.js` buffers an intent it
 * could not put in the iframe URL (the panel was already open) and flushes it here.
 */
export function announceReady() {
  postToParent({ type: "ready" });
}

/** Relay the state of the voice leg so the host can read it back in `HeyUpChat.state()`. */
export function reportCallState(status: string) {
  postToParent({ type: "call", status });
}

/**
 * A full name into its two halves. The host usually holds one string ("Marie Dupont")
 * while the customer record — and the form the visitor would otherwise fill — wants
 * both, so the first token is the given name and whatever follows is the family name.
 * Explicit `given_name` / `family_name` always win over this guess.
 */
function splitName(full: string): { given_name?: string; family_name?: string } {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return {};
  if (parts.length === 1) return { given_name: parts[0] };
  return { given_name: parts[0], family_name: parts.slice(1).join(" ") };
}

/** Build an identity from loose input, dropping everything empty. `null` if nothing is left. */
function toIdentity(
  raw:
    | {
        name?: string | null;
        given_name?: string | null;
        family_name?: string | null;
        email?: string | null;
        phone?: string | null;
      }
    | null
    | undefined,
): EmbedIdentity | null {
  if (!raw) return null;
  const fromName = raw.name ? splitName(String(raw.name)) : {};
  const identity: EmbedIdentity = {
    given_name: (raw.given_name || fromName.given_name || "").trim() || undefined,
    family_name: (raw.family_name || fromName.family_name || "").trim() || undefined,
    email: (raw.email || "").trim() || undefined,
    phone: (raw.phone || "").trim() || undefined,
  };
  return Object.values(identity).some(Boolean) ? identity : null;
}

/**
 * Widget options — set once, at iframe load, and true of every thread the visitor
 * opens from inside the panel. Everything else in the URL is the *intent*, which is
 * spent on the conversation it was given for.
 */
const WIDGET_OPTION_PARAMS = ["sound", "theme"] as const;

/** The options half of a query string, for a link from one thread to the next. */
export function widgetOptionsSearch(search: string): string {
  const from = new URLSearchParams(search);
  const kept = new URLSearchParams();
  for (const key of WIDGET_OPTION_PARAMS) {
    const value = from.get(key);
    if (value) kept.set(key, value);
  }
  const query = kept.toString();
  return query ? `?${query}` : "";
}

/**
 * The intent the widget was opened with, as `embed.js` wrote it into the iframe URL.
 * Read once per loaded conversation — replaying it on every render would re-send an
 * OTP for every re-render of the page.
 */
export function readIntentFromUrl(search?: string): EmbedIntent {
  const params = new URLSearchParams(
    search ?? (typeof window === "undefined" ? "" : window.location.search),
  );
  return {
    // Only ever "start it" here — a page that has just loaded has no call to hang up,
    // and reading an absent parameter as `false` would ask the chat to end one.
    audio: params.get("audio") === "1" ? true : undefined,
    message: params.get("message") || undefined,
    draft: params.get("draft") || undefined,
    booking: params.get("booking") === "1" || undefined,
    fromUrl: true,
    identity: toIdentity({
      name: params.get("user_name"),
      given_name: params.get("user_given_name"),
      family_name: params.get("user_family_name"),
      email: params.get("user_email"),
      phone: params.get("user_phone"),
    }),
    verify: params.get("verify") !== "0",
    // `user_hash` is what the embed writes and `/start` consumes; `signed=1` is the
    // form an older embed carried. Either says the same thing: a contact was vouched
    // for, so the phone in it is not the one to put through the OTP.
    signed: !!params.get("user_hash") || params.get("signed") === "1",
  };
}

/**
 * Intents pushed by the host *after* the iframe was loaded (`HeyUpChat({ audio: true })`
 * on an already-open panel). Returns the unsubscribe function.
 */
export function onParentIntent(
  handler: (intent: EmbedIntent) => void,
): () => void {
  const onMessage = (event: MessageEvent) => {
    const data = event.data;
    if (!data || data.source !== "heyup-parent" || data.type !== "intent") return;
    handler({
      audio: typeof data.audio === "boolean" ? data.audio : undefined,
      message: typeof data.message === "string" ? data.message : undefined,
      draft: typeof data.draft === "string" ? data.draft : undefined,
      booking: data.booking === true || undefined,
      identity: toIdentity(data.customer),
      verify: data.verify !== false,
      signed: !!data.signed,
    });
  };
  window.addEventListener("message", onMessage);
  return () => window.removeEventListener("message", onMessage);
}
